import {
  ContactCreateRequest,
  ContactCreateResponse,
  ContactDetailResponse,
  ContactUpdateRequest,
  ContactUpdateResponse,
  PaginatedContactResponse
} from "./contact.domain";

/**
 * Contact Repository Port
 *
 * Define las operaciones de persistencia para contactos.
 * Esta es una abstracción (puerto) que será implementada por adaptadores concretos
 * (HTTP, Mock, LocalStorage, etc.)
 *
 * @abstract
 */
export abstract class ContactRepository {
  /**
   * Obtiene la lista paginada de contactos
   *
   * @param page - Número de página (1-indexed)
   * @param pageSize - Cantidad de items por página
   * @param filters - Filtros opcionales (búsqueda, país, ordenamiento)
   * @returns Promise con respuesta paginada
   *
   * @example
   * ```typescript
   * const response = await repository.getContacts(1, 20, { search: 'Juan' });
   * console.log(response.totalItems); // 45
   * ```
   */
  abstract getContacts(
    page: number,
    pageSize: number
  ): Promise<PaginatedContactResponse>;

  /**
   * Obtiene un contacto por su ID
   *
   * @param id - ID único del contacto
   * @returns Promise con el contacto encontrado
   * @throws {Error} Si el contacto no existe
   *
   * @example
   * ```typescript
   * const contact = await repository.getContactById('contact-123');
   * console.log(contact.contactName); // 'Juan'
   * ```
   */
  abstract getContactById(id: string): Promise<ContactDetailResponse>;

  /**
   * Crea un nuevo contacto
   *
   * @param data - Datos del contacto a crear
   * @returns Promise con el contacto creado
   *
   * @example
   * ```typescript
   * const newContact = await repository.createContact({
   *   contactName: 'María',
   *   contactPaternalLastName: 'García',
   *   contactMaternalLastName: 'López',
   *   contactCountry: 'Perú',
   *   contactCountryCode: 'PE'
   * });
   * ```
   */
  abstract createContact(data: ContactCreateRequest): Promise<ContactCreateResponse>;

  /**
   * Actualiza un contacto existente
   *
   * @param id - ID del contacto a actualizar
   * @param data - Datos a actualizar (parcial)
   * @returns Promise con el contacto actualizado
   *
   * @example
   * ```typescript
   * const updated = await repository.updateContact('contact-123', {
   *   contactEmail: 'nuevo@email.com'
   * });
   * ```
   */
  abstract updateContact(id: string, data: ContactUpdateRequest): Promise<ContactUpdateResponse>;

  /**
   * Elimina un contacto
   *
   * @param id - ID del contacto a eliminar
   * @returns Promise que se resuelve cuando se completa la eliminación
   *
   * @example
   * ```typescript
   * await repository.deleteContact('contact-123');
   * console.log('Contacto eliminado');
   * ```
   */
  abstract deleteContact(id: string): Promise<void>;
}
